import { type MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { Link } from '@fly/core';
import { PACKAGE_MANAGERS } from '@fly/shared';
import {
  FlyAccordion,
  FlyTimeago,
  SkeletonConfig,
  StringKeysOf,
} from '@fly/shared-ui';
import { FlySvgSprite } from '@fly/svg';
import { Skeleton } from '@radix-ui/themes';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import type { ArtifactVersion } from '../../store/registry.service';
import { ArtifactVersionActions } from './artifactVersionActions';
import { ArtifactFiles } from './components/ArtifactFiles';
import { ArtifactVersionCoTags } from './components/ArtifactVersionCoTags';
import { ArtifactVersionDistributionBadge } from './components/ArtifactVersionDistributionBadge';
import { useArtifactVersionCoTagsExpanded } from './components/useArtifactVersionCoTagsExpanded';

const columnHelper = createColumnHelper<ArtifactVersion>();

const artifactVersionSkeletonConfig: SkeletonConfig = [
  {
    items: [
      { width: '50%', height: '8px' },
      {
        width: '120px',
        height: '8px',
        className: 'ml-auto relative top-[10px]',
      },
    ],
    className: 'flex',
  },
  {
    items: [{ width: '170px', height: '8px' }],
  },
];
export const ARTIFACT_VERSIONS_SEARCH_KEY = 'name' as const;

export type UseArtifactVersionsColumnsResult = {
  columns: ColumnDef<ArtifactVersion>[];
  searchKey: StringKeysOf<ArtifactVersion>;
};

export function useArtifactVersionsColumns(
  data: ArtifactVersion[],
  isLoading: boolean,
  filteredCount?: number | null
): UseArtifactVersionsColumnsResult {
  const { t, i18n } = useTranslation();
  const { artifactName, packageType } = useParams();
  const displayCount = filteredCount != null ? filteredCount : data.length;
  const { isExpanded: isCoTagsExpanded, toggleExpanded: toggleCoTagsExpanded } =
    useArtifactVersionCoTagsExpanded();

  const handleCoTagsToggle = useCallback(
    (rowKey: string, event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggleCoTagsExpanded(rowKey);
    },
    [toggleCoTagsExpanded]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: ARTIFACT_VERSIONS_SEARCH_KEY,
          meta: {
            className: 'p-0 w-full',
            skeletonConfig: artifactVersionSkeletonConfig,
          },
          header: () => (
            <div className='font-bold p-3 flex items-center text-size-fly-2 text-custom-12'>
              <FlySvgSprite name={packageType!} size={16} className='mr-2' />
              <span>{artifactName}</span>
              <FlySvgSprite name='dot' size={16} />
              <Skeleton loading={isLoading}>
                <span className='text-slateA-11 font-normal text-size-fly-2'>
                  {t('app.dashboard.artifacts.version_count', {
                    count: displayCount,
                  })}
                </span>
              </Skeleton>
            </div>
          ),
          cell: ({ row }) => {
            const version = row.original;
            const workflowRun = version?.releases?.[0];

            const isGenericPackageType =
              packageType === PACKAGE_MANAGERS.GENERIC;

            const rowContent = (
              <div
                className={`flex items-center justify-between gap-fly-3 py-fly-3 ${isGenericPackageType ? 'pl-0 pr-fly-4' : 'px-fly-4'}`}
              >
                <div className='text-slate-12 min-w-0 flex max-w-full flex-col gap-fly-2 text-size-fly-2'>
                  <div className='min-h-6 flex items-center gap-fly-2'>
                    <ArtifactVersionCoTags
                      versionName={version.name}
                      coTags={version.tags ?? []}
                      expanded={isCoTagsExpanded(version.name)}
                      onToggleExpanded={(event) =>
                        handleCoTagsToggle(version.name, event)
                      }
                    />
                    {version.distribution != null && (
                      <ArtifactVersionDistributionBadge
                        distribution={version.distribution}
                        artifactName={artifactName!}
                        versionName={version.name}
                        packageType={packageType!}
                      />
                    )}
                  </div>
                  <div className='text-slateA-11 flex flex-wrap items-center'>
                    {workflowRun && (
                      <>
                        <span>
                          {t('app.dashboard.releases.release', {
                            defaultValue: 'Release',
                          })}
                          :{' '}
                          <Link
                            href={workflowRun.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            weight='bold'
                            onClick={(e) => e.stopPropagation()}
                          >
                            {workflowRun.name}-{workflowRun.number}
                          </Link>
                        </span>
                        <FlySvgSprite name='dot' size={16} />
                      </>
                    )}
                    <span>
                      {version.uploadedLocally
                        ? t('app.dashboard.artifacts.uploaded_locally', {
                            defaultValue: 'Uploaded locally',
                          })
                        : t('app.dashboard.artifacts.uploaded', {
                            defaultValue: 'Uploaded',
                          })}{' '}
                      {version.uploadedBy &&
                        t('app.dashboard.artifacts.uploaded_by', {
                          defaultValue: 'by {{username}}',
                          username: version.uploadedBy,
                        })}
                    </span>
                  </div>
                </div>
                <div className='flex shrink-0 items-center gap-fly-2'>
                  <FlyTimeago
                    className='px-fly-3 text-size-fly-2'
                    date={version.lastUploadTimestamp}
                    language={i18n.language}
                  />
                  <ArtifactVersionActions
                    artifactVersion={version.name}
                    packageType={packageType!}
                    name={artifactName!}
                  />
                </div>
              </div>
            );

            if (!isGenericPackageType) {
              return rowContent;
            }

            return (
              <FlyAccordion
                trigger={null}
                chevronClosedDirection='right'
                visibleContent={rowContent}
                inlineVisibleContent
                triggerClassName='px-fly-4 py-fly-3 mt-[7px]'
                className='w-full'
              >
                <ArtifactFiles
                  name={artifactName!}
                  packageVersion={version.name}
                />
              </FlyAccordion>
            );
          },
        }),
      ] as ColumnDef<ArtifactVersion>[],
    [
      displayCount,
      isLoading,
      t,
      i18n.language,
      artifactName,
      packageType,
      isCoTagsExpanded,
      handleCoTagsToggle,
    ]
  );

  return {
    columns,
    searchKey: ARTIFACT_VERSIONS_SEARCH_KEY as StringKeysOf<ArtifactVersion>,
  };
}
